import {
  SearchRequestScalarFieldEnumSchema as PrismaSearchRequestScalarFieldEnumSchema,
  SearchRequestSchema as PrismaSearchRequestSchema,
  SearchResultScalarFieldEnumSchema as PrismaSearchResultScalarFieldEnumSchema,
  SearchResultSchema as PrismaSearchResultSchema,
  SharedSearchSchema,
} from '@retro-ui/prisma';
import { buildOrderBySchema, PaginationSchema } from '../common';
import {
  SearchCreditCycle,
  SearchDetailLevel,
  SearchReactionConfidenceThreshold,
  SearchInvalidTargetStrategy,
  SearchLength,
  SearchResultStatus,
  SearchSource,
  SearchSynthesisScale,
  REACTION_CONFIDENCE_THRESHOLD_DEFAULT,
  MIN_REFERENCE_COUNT_DEFAULT,
  MIN_REFERENCE_COUNT_RANGE,
  MIN_N_PROVIDERS_RANGE,
  MIN_N_PROVIDERS_DEFAULT,
  MAX_N_ATOMS_DEFAULT,
  MAX_PRICE_DEFAULT,
  MAX_PRICE_PER_GRAM_DEFAULT,
} from '@retro-ui/shared';
import { RefinementCtx, z } from 'zod';

export type SearchRequest = z.infer<typeof PrismaSearchRequestSchema>;

export const SearchRequestWithSharedSchema = PrismaSearchRequestSchema.extend({
  sharedWithUsers: z.array(SharedSearchSchema),
});
export type SearchRequestWithShared = z.infer<typeof SearchRequestWithSharedSchema>;

export const SearchStatusSchema = z.object({
  queued: z.number(),
  running: z.number(),
  finished: z.number(),
  error: z.number(),
});
export type SearchStatus = z.infer<typeof SearchStatusSchema>;

const SearchStatisticsSchema = z.object({
  reactionCount: z.object({
    min: z.number().nullable(),
    max: z.number().nullable(),
  }),
  price: z.object({
    min: z.number().nullable(),
    max: z.number().nullable(),
  }),
});
export type SearchStatistics = z.infer<typeof SearchStatisticsSchema>;

export const DatasetSearchParamSchema = z
  .object({
    id: z.string(),
    selected_suppliers: z.array(z.string()).optional(),
  })
  .strict();
export type DatasetSearchParam = z.infer<typeof DatasetSearchParamSchema>;

export const SearchPresetSchema = z.object({
  model: z.string(),
  length: z.enum(
    [SearchLength.SHORT as const, SearchLength.REGULAR as const, SearchLength.SINGLE_STEP as const],
    {
      invalid_type_error: 'Invalid search length.',
    }
  ),
  synthesis_scale: z.enum(
    [
      SearchSynthesisScale.KILOGRAM as const,
      SearchSynthesisScale.MOLAR as const,
      SearchSynthesisScale.MILLIMOLAR as const,
      SearchSynthesisScale.MICROMOLAR as const,
    ],
    {
      invalid_type_error: 'Invalid synthesis scale.',
    }
  ),
  datasets: z.array(DatasetSearchParamSchema),
  availability_tier: z.number().int().gte(1).lte(5),
  reaction_confidence_threshold: z
    .enum([SearchReactionConfidenceThreshold.HIGH, SearchReactionConfidenceThreshold.LOW])
    .default(REACTION_CONFIDENCE_THRESHOLD_DEFAULT),
  min_reference_count: z
    .number()
    .int()
    .gte(MIN_REFERENCE_COUNT_RANGE[0])
    .lte(MIN_REFERENCE_COUNT_RANGE[1])
    .default(MIN_REFERENCE_COUNT_DEFAULT),
  excluded_smiles: z.array(z.string()),
  min_n_providers: z
    .number()
    .int()
    .gte(MIN_N_PROVIDERS_RANGE[0])
    .lte(MIN_N_PROVIDERS_RANGE[1])
    .default(MIN_N_PROVIDERS_DEFAULT),
  max_n_atoms: z.number().int().gte(0).default(MAX_N_ATOMS_DEFAULT),
  max_price: z.number().int().gte(0).default(MAX_PRICE_DEFAULT),
  max_price_per_gram: z.number().int().gte(0).default(MAX_PRICE_PER_GRAM_DEFAULT),
});
export type SearchPreset = z.infer<typeof SearchPresetSchema>;

const SearchDetailLevelSchema = z.enum(
  [SearchDetailLevel.SCORE, SearchDetailLevel.BEST_PATH, SearchDetailLevel.ALL_PATHS],
  {
    invalid_type_error: 'Invalid detail level.',
  }
);

const SearchParamsSchema = z.object({
  detail_level: SearchDetailLevelSchema,
  presets: SearchPresetSchema.partial(),
  overrides: SearchPresetSchema.partial().passthrough().optional(),
});
export type SearchParams = z.infer<typeof SearchParamsSchema>;

const SearchTargetsMetadataSchema = z.record(
  z.object({
    name: z.string(),
  })
);

const SearchWhereSchema = z.object({
  name: z.object({ contains: z.string() }),
  source: z.object({ in: z.array(z.enum([SearchSource.UI, SearchSource.APIv2])) }),
  isRemoved: z.preprocess(value => (value === 'false' ? false : value), z.coerce.boolean()),
  createdAt: z.object({ gte: z.coerce.date(), lte: z.coerce.date() }).partial(),
});

const SearchOrderBySchema = buildOrderBySchema(
  PrismaSearchRequestScalarFieldEnumSchema.extract(['name', 'size', 'createdAt'])
);

export const SearchQuerySchema = PaginationSchema.extend({
  orderBy: SearchOrderBySchema.optional(),
  where: SearchWhereSchema.partial().optional(),
});

export const SearchIdSchema = PrismaSearchRequestSchema.shape.id.uuid();

const SearchResultWhereSchema = z
  .object({
    id: z.object({
      in: z.array(z.string()),
    }),
    status: z.object({
      in: z.array(
        z.union([
          z.literal(SearchResultStatus.COMPLETED),
          z.literal(SearchResultStatus.FAILED),
          z.literal(SearchResultStatus.RUNNING),
        ])
      ),
    }),
    targetFilter: z.string().optional(),
    result: z.object({
      lte: z.coerce.number(),
      gte: z.coerce.number(),
    }),
    reactionCount: z.object({
      lte: z.coerce.number(),
      gte: z.coerce.number(),
    }),
    price: z
      .object({
        lte: z.coerce.number(),
        gte: z.coerce.number(),
      })
      .partial(),
  })
  .partial();

const SearchResultOrderBySchema = buildOrderBySchema(
  PrismaSearchResultScalarFieldEnumSchema.extract([
    'result',
    'reactionCount',
    'referenceCount',
    'price',
  ])
);

export const SearchResultQuerySchema = PaginationSchema.extend({
  orderBy: SearchResultOrderBySchema.optional(),
  where: SearchResultWhereSchema.optional(),
});
export type SearchResultQuery = z.infer<typeof SearchResultQuerySchema>;

export const SearchResultIdSchema = PrismaSearchResultSchema.shape.id.uuid();

export const SearchMetadataSchema = z.object({
  status: SearchStatusSchema,
  statistics: SearchStatisticsSchema,
  creditsDue: z.coerce.bigint().nullable(),
  shareInfo: z.object({
    sharedWithMe: z.boolean(),
    ownerEmail: z.string().optional(),
    userIds: z.array(z.string()),
  }),
});
export type SearchMetadata = z.infer<typeof SearchMetadataSchema>;

export const SearchWithMetadataSchema = PrismaSearchRequestSchema.merge(
  SearchMetadataSchema.partial()
);
export type SearchWithMetadata = z.infer<typeof SearchWithMetadataSchema>;

export const PublicSearchRequest = PrismaSearchRequestSchema.omit({
  name: true,
  presetName: true,
});
export const PublicSearchWithMetadataSchema = PublicSearchRequest.merge(
  SearchMetadataSchema.partial()
);

export const PublicSearchBasicInfoSchema = PrismaSearchRequestSchema.pick({
  id: true,
  name: true,
  createdAt: true,
  size: true,
}).extend({
  ownerEmail: z.string(),
  creditsDue: SearchMetadataSchema.shape.creditsDue,
  model: SearchParamsSchema.shape.presets.shape.model,
  length: SearchParamsSchema.shape.presets.shape.length,
});

export const SmilesSchema = z
  .string()
  .min(1, 'SMILES must contain at least 1 character.')
  .max(500, 'SMILES must contain up to 500 characters.');

export function validateTargetMetadata(targetMetadataKey: 'targets_metadata' | 'targetsMetadata') {
  return function (createSearchSchema: any, ctx: RefinementCtx) {
    const targetMetadata = createSearchSchema[targetMetadataKey];
    if (!targetMetadata) {
      return;
    }
    const invalidIndices = Object.keys(targetMetadata).filter(
      key => !createSearchSchema.targets[Number(key)]
    );
    if (invalidIndices.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Target metadata keys must correspond to target array indices. Invalid indices: ${invalidIndices.join(
          ', '
        )}`,
      });
    }
  };
}

export const TargetSchema = z.object({
  smiles: SmilesSchema,
  name: z.string().optional(),
});
export type Target = z.infer<typeof TargetSchema>;

export const RunSearchDataSchema = z.object({
  rerunBatchId: z.string(),
  name: z.string().min(3),
  targets: z
    .array(SmilesSchema)
    .min(1, 'At least 1 target has to be specified.')
    .max(100000, 'At most 100 000 targets can be specified at once.'),
  targetsMetadata: SearchTargetsMetadataSchema,
  detailLevel: SearchDetailLevelSchema,
  invalidTargetStrategy: z.enum(
    [SearchInvalidTargetStrategy.PASS, SearchInvalidTargetStrategy.REJECT],
    {
      invalid_type_error: 'Invalid value for invalid target strategy.',
    }
  ),
  priority: z.number().int().gte(1).lte(10),
  presetName: z.string().min(1),
  inputParams: SearchPresetSchema.partial(),
  overrides: SearchPresetSchema.partial().passthrough().optional(),
  source: z.enum([SearchSource.UI, SearchSource.APIv2], {
    invalid_type_error: 'Invalid search source.',
  }),
});

export const SearchCreditOverviewSchema = z.object({
  balance: z.coerce.bigint(),
  cycle: z.enum([SearchCreditCycle.NONE, SearchCreditCycle.MONTH]),
  creditsPerCycle: z.coerce.bigint().gte(0n),
});
export type SearchCreditOverview = z.infer<typeof SearchCreditOverviewSchema>;

export const SmilesInfoSchema = z
  .object({
    length: z.number().optional(),
    numberOfAtoms: z.number().optional(),
    numberOfChargedAtoms: z.number().optional(),
    numberOfMetalAtoms: z.number().optional(),
    numberOfExplicitHydrogens: z.number().optional(),
    numberOfChiralAtoms: z.number().optional(),
  })
  .partial();
export type SmilesInfo = z.infer<typeof SmilesInfoSchema>;
